import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useSession } from '../state/session'
import { useToday } from '../state/today'
import { formatDuration, formatTime } from '../lib/format'
import { SiteChangeSheet } from './SiteChangeSheet'
import { Button } from './ui'

/**
 * The live band.
 *
 * Ink bar, brand type, hazard tape along the bottom — the logo's own device.
 * Somebody should be able to tell from across a room whether their phone says
 * they are on the clock, and this is the inverse of a primary button so the two
 * are never confused.
 */
function StatusBand() {
  const { state } = useToday()
  const [, forceTick] = useState(0)

  // The running total has to tick without refetching.
  useEffect(() => {
    if (!state?.openEntry) return
    const id = setInterval(() => forceTick((n) => n + 1), 30_000)
    return () => clearInterval(id)
  }, [state?.openEntry])

  if (!state) return null

  const open = state.openEntry
  const elapsed = open ? Date.now() - Date.parse(open.clockInAt) : 0

  if (!open) {
    return (
      <div className="safe-top border-b border-line bg-raised">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-2.5">
          <span className="label">Off the clock</span>
          <span className="font-mono text-xs text-graphite-2 tnum">
            {formatDuration(state.totalWorkedMs)} today
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="live-bar safe-top">
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-2.5">
        <div className="min-w-0">
          <p className="label text-brand/70">On the clock</p>
          <p className="truncate text-sm font-extrabold">{open.siteName}</p>
        </div>
        <p className="shrink-0 font-mono text-2xl font-semibold tnum">{formatDuration(elapsed)}</p>
      </div>
      <div className="hazard-edge h-1" data-live="true" aria-hidden />
    </div>
  )
}

function ConnectionBanner() {
  const { online, pending } = useToday()

  if (online && pending.length === 0) return null

  return (
    <div
      className={`px-4 py-2 text-center text-xs font-bold ${
        online ? 'bg-ink text-brand' : 'bg-graphite text-concrete'
      }`}
      role="status"
    >
      {online
        ? `Syncing ${pending.length} queued ${pending.length === 1 ? 'item' : 'items'}…`
        : `No signal — ${pending.length === 0 ? 'work is saved on this phone' : `${pending.length} queued`}`}
    </div>
  )
}

function UpdateBanner() {
  const [apply, setApply] = useState<(() => void) | null>(null)

  useEffect(() => {
    const onUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ apply: () => void }>).detail
      setApply(() => detail.apply)
    }
    window.addEventListener('wm:sw-update', onUpdate)
    return () => window.removeEventListener('wm:sw-update', onUpdate)
  }, [])

  if (!apply) return null

  return (
    <div className="flex items-center justify-between gap-3 bg-brand px-4 py-2 text-ink">
      <span className="text-xs font-bold">A new version is ready.</span>
      <button type="button" onClick={apply} className="text-xs font-bold underline underline-offset-2">
        Reload
      </button>
    </div>
  )
}

interface Tab {
  to: string
  label: string
  icon: React.ReactNode
  end?: boolean
}

const iconClass = 'size-5'

const TABS: Tab[] = [
  {
    to: '/',
    label: 'Today',
    end: true,
    icon: (
      <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/log',
    label: 'Log',
    icon: (
      <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 3h14v18l-7-3.5L5 21z" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/inventory',
    label: 'Gear',
    icon: (
      <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 8l9-5 9 5-9 5z" strokeLinejoin="round" />
        <path d="M3 8v8l9 5 9-5V8" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/docs',
    label: 'Docs',
    icon: (
      <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 3h8l4 4v14H6z" strokeLinejoin="round" />
        <path d="M14 3v4h4M9 12h6M9 16h6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/settings',
    label: 'You',
    icon: (
      <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="8" r="3.5" />
        <path d="M4.5 20a7.5 7.5 0 0115 0" strokeLinecap="round" />
      </svg>
    ),
  },
]

const ADMIN_TAB: Tab = {
  to: '/admin',
  label: 'Office',
  icon: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="16" rx="1" />
      <path d="M3 9h18M9 9v11" />
    </svg>
  ),
}

function BottomNav() {
  const { user } = useSession()
  const tabs = user?.role === 'admin' ? [...TABS, ADMIN_TAB] : TABS

  return (
    <nav
      className="safe-bottom sticky bottom-0 z-30 border-t border-line bg-raised"
      aria-label="Main"
    >
      <div className="mx-auto flex max-w-3xl">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `flex min-w-0 flex-1 flex-col items-center gap-1 py-2 text-[0.625rem] font-bold tracking-widest uppercase transition-colors ${
                isActive ? 'text-graphite' : 'text-graphite-2'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {/* The current tab gets a brand block behind its icon. A large
                    filled shape carries the brand colour; a hairline of it
                    would not be visible on the light surface. */}
                <span
                  className={`flex size-8 items-center justify-center rounded ${
                    isActive ? 'bg-brand text-ink' : 'text-current'
                  }`}
                >
                  {tab.icon}
                </span>
                <span className="max-w-full truncate">{tab.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}

export function AppShell() {
  const { state, error, refresh } = useToday()
  const { settings } = useSession()
  const location = useLocation()
  const firstRender = useRef(true)

  // Navigating back to a screen re-reads the day, so an action taken elsewhere
  // (or on another device) is reflected. The provider already loads on mount,
  // so skip the first pass rather than fetching twice.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <div className="safe-frame flex min-h-dvh flex-col">
      <StatusBand />
      <UpdateBanner />
      <ConnectionBanner />

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 pt-4 pb-8">
        {error && (
          <div className="mb-4 flex items-center justify-between gap-3 border border-line-strong bg-well px-3 py-2 text-sm">
            <span>{error}</span>
            <Button size="md" onClick={() => void refresh()} className="min-h-9 px-3 text-xs">
              Retry
            </Button>
          </div>
        )}
        <Outlet />
      </main>

      {state && (
        <p className="pb-2 text-center font-mono text-[10px] tracking-widest text-graphite-2 uppercase">
          {settings.orgName} · {formatTime(state.now, state.timezone)} {state.timezone}
        </p>
      )}

      <BottomNav />
      <SiteChangeSheet />
    </div>
  )
}
